import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useBooking } from "./BookingContext";

// Content interface matching your Supabase booking_engine_content exactly
export interface BookingContent {
  id?: string;
  hotelId: string;
  language: string;

  // Navigation & Branding
  welcomeTitle?: string;

  // Search/Booking Page
  heroTitle?: string;

  // Accommodation Section
  aboutTitle?: string;
  aboutContent?: string;

  // Extras Section
  extrasTitle?: string;
  extrasSubtitle?: string;

  // Personal Information Section
  personalInfoTitle?: string;

  // Confirmation Section
  confirmationTitle?: string;
  confirmationSubtitle?: string;

  // Legal & Footer
  footerText?: string;
  termsAndConditions?: string;
  privacyPolicy?: string;
  cancellationPolicy?: string;
}

// Default content matching your Supabase data structure
const defaultContent: BookingContent = {
  hotelId: "",
  language: "",
  welcomeTitle: "",
  heroTitle: "",
  aboutTitle: "",
  aboutContent: "",
  extrasTitle: "",
  extrasSubtitle: "",
  personalInfoTitle: "",
  confirmationTitle: "",
  confirmationSubtitle: "",
  footerText: "",
  termsAndConditions: "",
  privacyPolicy: "",
  cancellationPolicy: "",
};

interface ContentContextType {
  content: BookingContent;
  isLoading: boolean;
  error: string | null;
}

const ContentContext = createContext<ContentContextType>({
  content: defaultContent,
  isLoading: false,
  error: null,
});

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedHotel } = useBooking();
  const [content, setContent] = useState<BookingContent>(defaultContent);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log(selectedHotel, "see selectedHotel");

  // Function to load content from database
  const loadContent = async () => {
    console.log(
      "📝 ContentProvider loadContent called with selectedHotel:",
      selectedHotel
    );

    if (!selectedHotel) {
      console.log("📝 Using default content - no selected hotel");
      setContent(defaultContent);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log("📝 Loading content for hotel:", selectedHotel.name);

      // Get booking engine content using selectedHotel.id
      const { data: contentData, error: contentError } = await supabase
        .from("booking_engine_content")
        .select("*")
        .eq("hotel_id", selectedHotel.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (contentError && contentError.code !== "PGRST116") {
        console.error("❌ Error loading booking content:", contentError);
        console.log("ℹ️ Using default content due to content error");
        setContent(defaultContent);
        setIsLoading(false);
        return;
      }

      let newContent = { ...defaultContent, hotelId: selectedHotel.id };

      if (contentData) {
        console.log("✅ Booking engine content found:", contentData);

        // Map database fields to content properties
        newContent = {
          id: contentData.id,
          hotelId: contentData.hotel_id,
          language: contentData.language,
          welcomeTitle:
            contentData.welcome_title || defaultContent.welcomeTitle,
          heroTitle: contentData.hero_title || defaultContent.heroTitle,
          aboutTitle: contentData.about_title || defaultContent.aboutTitle,
          aboutContent:
            contentData.about_content || defaultContent.aboutContent,
          extrasTitle: contentData.extras_title || defaultContent.extrasTitle,
          extrasSubtitle:
            contentData.extras_subtitle || defaultContent.extrasSubtitle,
          personalInfoTitle:
            contentData.personal_info_title || defaultContent.personalInfoTitle,
          confirmationTitle:
            contentData.confirmation_title || defaultContent.confirmationTitle,
          confirmationSubtitle:
            contentData.confirmation_subtitle || defaultContent.confirmationSubtitle,
          footerText: contentData.footer_text,
          termsAndConditions: contentData.terms_and_conditions,
          privacyPolicy: contentData.privacy_policy,
          cancellationPolicy: contentData.cancellation_policy,
        };
      } else {
        console.log("ℹ️ No booking engine content found, using defaults");
      }

      console.log("📝 Applied content:", newContent);
      setContent(newContent);
    } catch (err) {
      console.error("❌ Content loading error:", err);
      setError(err instanceof Error ? err.message : "Failed to load content");
      // Fallback to default content
      setContent(defaultContent);
    } finally {
      setIsLoading(false);
    }
  };

  // Load content when selectedHotel changes
  useEffect(() => {
    if (selectedHotel?.id) {
      loadContent();
    }
  }, [selectedHotel?.id]); // Only depend on hotel ID

  // Set up real-time updates for content
  useEffect(() => {
    console.log(
      "🔄 Setting up real-time content updates, selectedHotel:",
      selectedHotel
    );

    if (!selectedHotel?.id) {
      console.log("🔄 No selectedHotel ID, skipping content real-time setup");
      return;
    }

    const setupRealtimeUpdates = async () => {
      console.log(
        "🔄 setupRealtimeContentUpdates called for selectedHotel:",
        selectedHotel.name
      );

      // Use selectedHotel.id directly
      const hotelId = selectedHotel.id;

      console.log(
        "🔄 Setting up real-time content updates for hotel ID:",
        hotelId
      );

      const channel = supabase
        .channel(`booking-engine-content-updates-${hotelId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "booking_engine_content",
            filter: `hotel_id=eq.${hotelId}`,
          },
          (payload) => {
            console.log("📝 Real-time content update received:", payload);

            if (
              payload.eventType === "UPDATE" ||
              payload.eventType === "INSERT"
            ) {
              const contentData = payload.new;
              const newContent: BookingContent = {
                id: contentData.id,
                hotelId: contentData.hotel_id,
                language: contentData.language,
                welcomeTitle: contentData.welcome_title,
                heroTitle: contentData.hero_title,
                aboutTitle: contentData.about_title,
                aboutContent: contentData.about_content,
                extrasTitle: contentData.extras_title,
                extrasSubtitle: contentData.extras_subtitle,
                personalInfoTitle: contentData.personal_info_title,
                confirmationTitle: contentData.confirmation_title,
                confirmationSubtitle: contentData.confirmation_subtitle,
                footerText: contentData.footer_text,
                termsAndConditions: contentData.terms_and_conditions,
                privacyPolicy: contentData.privacy_policy,
                cancellationPolicy: contentData.cancellation_policy,
              };

              console.log("📝 Applying real-time content update:", newContent);
              setContent(newContent);
            }
          }
        )
        .subscribe((status) => {
          console.log("🔄 Real-time content subscription status:", status);
        });

      console.log(
        "✅ Real-time content subscription set up successfully for hotel ID:",
        hotelId
      );

      return () => {
        console.log("🔄 Cleaning up real-time content subscription");
        supabase.removeChannel(channel);
      };
    };

    const cleanup = setupRealtimeUpdates();
    return () => {
      cleanup?.then((cleanupFn) => cleanupFn?.());
    };
  }, [selectedHotel?.id]); // Only depend on hotel ID

  return (
    <ContentContext.Provider value={{ content, isLoading, error }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = (): ContentContextType => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContent must be used within a ContentProvider");
  }
  return context;
};
